import { ClockIcon, PaperAirplaneIcon } from '@heroicons/react/20/solid';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';

import { type MailboxProvider } from '~/api/graphql';
import {
  RESCHEDULE_UNAVAILABLE_TITLE,
  formatSendTime,
  scheduledEmailHelper
} from '~/lib/scheduled-send';

import { type SentEmail } from '~/components/workspaces/hooks/use-email-composer';

export const ScheduledEmailStatus = (props: {
  scheduledEmail: Pick<SentEmail, 'scheduledFor'>;
  provider?: MailboxProvider;
}) => {
  const scheduledFor = new Date(props.scheduledEmail.scheduledFor ?? Date.now());
  return (
    <div className="min-w-0 space-y-1 text-right text-xs text-neutral-500 dark:text-neutral-400">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Badge color="sky">
          <ClockIcon className="size-3" aria-hidden />
          Scheduled
        </Badge>
        <span>{`Sends ${formatSendTime(scheduledFor)}. You can close this page, it still goes out.`}</span>
      </div>
      {/* index.css gives every <p> prose-base, which overrides the inherited text-xs. */}
      <p className="text-xs">{scheduledEmailHelper(props.provider)}</p>
    </div>
  );
};

/**
 * Takes the place of Save and Send while an email is scheduled. Edit keeps the
 * schedule: the fields open and save as they are typed.
 */
export const ScheduledEmailActions = (props: {
  editing: boolean;
  /** False while scheduled sending is off: the email still goes out, but its time can't change. */
  canChangeSendTime: boolean;
  busy: boolean;
  /** Why Send now is off though the email could otherwise go, such as a blank template field. */
  sendNowDisabledReason?: string;
  onCancel: () => void;
  onToggleEdit: () => void;
  onReschedule: () => void;
  onSendNow: () => void;
}) => (
  <div className="flex flex-wrap items-center justify-end gap-2">
    <Button
      plain
      className="text-red-600 dark:text-red-400"
      disabled={props.busy}
      onClick={props.onCancel}
    >
      Cancel send
    </Button>
    <Button color="white" disabled={props.busy} onClick={props.onToggleEdit}>
      {props.editing ? 'Done editing' : 'Edit'}
    </Button>
    <Button
      color="white"
      disabled={props.busy || !props.canChangeSendTime}
      title={props.canChangeSendTime ? undefined : RESCHEDULE_UNAVAILABLE_TITLE}
      onClick={props.onReschedule}
    >
      Reschedule
    </Button>
    <Button
      color="sky"
      disabled={props.busy || !props.canChangeSendTime || !!props.sendNowDisabledReason}
      loading={props.busy}
      title={props.canChangeSendTime ? props.sendNowDisabledReason : RESCHEDULE_UNAVAILABLE_TITLE}
      onClick={props.onSendNow}
    >
      <PaperAirplaneIcon className="size-4" />
      Send now
    </Button>
  </div>
);
